import React, {
  Children,
  FunctionComponent,
  ReactNode,
  isValidElement,
  useEffect,
  useState,
} from 'react';

import { captureException } from '@sentry/browser';

import useAbEnrol from '../../hooks/useAbEnrol';
import { Variation } from '../../utils/abTests';

interface ExperimentProps {
  slug: string;
  force?: Variation;
}

interface Variations {
  A?: ReactNode;
  B?: ReactNode;
  C?: ReactNode;
  D?: ReactNode;
  E?: ReactNode;
}

export const Experiment: FunctionComponent<ExperimentProps> = ({
  slug,
  force,
  children,
}) => {
  const { enrolledInVariation } = useAbEnrol(slug);

  const [variations, setVariations] = useState<Variations>({
    A: null,
    B: null,
    C: null,
    D: null,
    E: null,
  });

  if (!(enrolledInVariation in variations)) {
    const message = 'Variation provided by ABService not supported';
    captureException(message);
    throw new Error(message);
  }

  useEffect(() => {
    if (Children.count(children) > 5) {
      const message = 'No more than 5 variations allowed';
      captureException(message);
      throw new Error(message);
    }

    Children.forEach(children, element => {
      if (!isValidElement(element)) {
        const message = 'One of your variations is not formatted correctly';
        captureException(message);
        throw new Error(message);
      }

      // This typing is horrible. I challenge you to do better.
      if ((element.type as any).displayName !== 'Variation') {
        const message = 'Please use the Variation component to wrap variations';
        captureException(message);
        throw new Error(message);
      }

      setVariations(variations => ({
        ...variations,
        [element.props.name]: element,
      }));
    });
  }, [children]);

  const component = force ? variations[force] : variations[enrolledInVariation];

  // We're already checking that the component is valid however typescript
  // doesn't infer this outside of the effect. Checking again to stop
  // typescript warnings
  return isValidElement(component) ? component : null;
};
